import React, {
  useState,
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";

import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";

import { useInterval } from "react-use";

import GridExports from "../components/GridExports";

import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";
import { GetObjectHashValue } from "shared/helpers/DataHelpers";
import { GetSecondsBetweenDates } from "shared/helpers/DateHelpers";

import { ControlSpinner } from "shared/components/spinners/ControlSpinner";
import { Alert } from "react-bootstrap";

import { useFetchServerGridData } from "./hooks/useFetchServerGridData";
import { useServerGridDataState } from "./hooks/useServerGridDataState";
import { useExportServerGridData } from "./hooks/useExportServerGridData";
import { useServerGridSelectedResultState } from "./hooks/useServerGridSelectedResultState";

import { useServerGridAggregates } from "./hooks/useServerGridAggregates";
import { CreateEnhancedChildrenForFirefox } from "../helpers/CreateEnhancedChildrenForFirefox";

const SharedMainSelectableServerGrid = forwardRef((props, ref) => {
  const {
    dataItemKey,
    styleOverride,
    gridButtons,
    pageSize = 50, // this will be the default page size
    secondsBeforeLoadingSpinner = 0,
    isSelectable = false,
    setServerGridParentData,
    aggregateColumnSettings,
    sendAggregatesParentCallback,
    onGridApiDataLoadedCallback, //This is the callback when the grid data has been loaded with specific parameters, so it is called only ONCE when the data is loaded
    onDataStateChangedCallback, //This will he called every time the grifd data state has changed, (ie. sort,filter, page, etc.) - in such a case onGridApiDataLoadedCallback will NOT be called
    useQueryOptions = null,
    onGridIsLoadingStatusChanged,
    gridErrorHandlerCallback,
    enableExportAggregates = false, // This will enable the export of aggregates in the export data
  } = props;

  const defaultButtonStyleClasses =
    "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary";

  const SELECTED_FIELD = "SelectedId";

  const exportReportOptions = {
    exportFileNamePrefix: props.exportFileNamePrefix,
    noGridExportOption: props.noGridExportOption,
    generateReportButtonTitle: props.generateReportButtonTitle,
    generateReportForSelectablesCallback:
      props.generateReportForSelectablesCallback,
  };

  const { gridDataState, onGridDataStateChange } = useServerGridDataState(
    props.apiSubmittedFormData,
    pageSize
  );

  const {
    exportColumns,
    exportFormat,
    fullExportData,
    runningExportData,
    InitiateExport: InitiateGridExport,
    ExportHasCompleted,
  } = useExportServerGridData(
    props.apiGetEndpoint,
    props.apiSubmittedFormData,
    gridDataState,
    gridErrorHandlerCallback,
    props.apiPostEndpoint
  );

  const [secondsToShowLoadingSpinner] = useState(
    secondsBeforeLoadingSpinner || 0
  );

  const [hasDelayedLoadingSpinner] = useState(secondsToShowLoadingSpinner > 0);

  const [timeOutPeriodPassed, setTimeOutPeriodPassed] = useState(false);

  //This will track if the loading spinner is running or not
  const [timerTickPeriodPassed, setTimerTickPeriodPassed] = useState("");

  const [timerStartedDateTime, setTimerStartedDateTime] = useState("");

  //while we are pulling new data, we want to show the old data until the new data is ready
  const [transitionDisplayData, setTransitionDisplayData] = useState();

  // const loadingTimeoutRef = useRef(null);

  const [headerCheckBox, setHeaderCheckBox] = useState(false);
  //const [currentDataStateHash, setCurrentDataStateHash] = useState("");

  //This will be used to identify if the grid data per the API arguments ha been loaded.
  //If not loaded, then we will always show the spinner
  //If loaded but being filtered or sorted, then we will have additional logic as to whether to show the spinner or not`
  const [gridApiParamsDataLoaded, setGridApiParamsDataLoaded] = useState(false);

  const [gridApiDataLoadedCallbackKey, setGridApiDataLoadedCallbackKey] =
    useState("");

  const [gridDataStateChangedCallbackKey, setGridDataStateChangedCallbackKey] =
    useState("");

  //This is the largest number of records that can be exported to Excel
  // const maxExportRecordsCount = 1048576;

  //const [runningExportData, setRunningExportData] = useState(false);

  const _grid = React.useRef();

  let {
    data: fetchedServerGridData,
    isLoading: fetchServerGridDataIsLoading,
    isRefetching,
    isError,
    error,
    isSuccess,
  } = useFetchServerGridData(
    props.apiGetEndpoint,
    props.apiSubmittedFormData,
    gridDataState,
    gridErrorHandlerCallback,
    setServerGridParentData,
    useQueryOptions,
    props.apiPostEndpoint
  );

  // if(setServerGridParentData){
  //   setServerGridParentData(serverGridData);
  // }

  useServerGridAggregates(
    fetchedServerGridData ? fetchedServerGridData.fieldTotals : null,
    aggregateColumnSettings,
    sendAggregatesParentCallback
  );

  let {
    gridResultState,
    onSelectionChange,
    SelectAll,
    UnSelectAll,
    GetSelectedGridDataKeys,
  } = useServerGridSelectedResultState(
    fetchedServerGridData,
    isSelectable,
    dataItemKey
  );

  function shouldShowSpinner() {
    if (runningExportData) {
      //console.log("runningExportData so should show spinner");
      return true;
    }

    if (isRefetching) {
      return true;
    }

    if (!fetchServerGridDataIsLoading) return false;

    //if there is no delay then we always show the spinner while waiting for the data
    if (!hasDelayedLoadingSpinner) {
      // console.log(
      //   "Not hasDelayedLoadingSpinner, so should show spinner result is",
      //   fetchServerGridDataIsLoading
      // );
      return fetchServerGridDataIsLoading;
    }

    if (fetchServerGridDataIsLoading && !gridApiParamsDataLoaded) {
      // console.log(
      //   "fetchServerGridDataIsLoading, and gridBindingData total less than 1 so should show spinner"
      // );
      return true;
    }

    // if we get here then we do have a delay
    // if we are loading and the total is 0, this means we are waiting for initial data to load and do not have the transition data to show
    // so we show the spinner
    // if (fetchServerGridDataIsLoading && gridBindingData.total < 1) {
    //   console.log(
    //     "fetchServerGridDataIsLoading, and gridBindingData total less than 1 so should show spinner"
    //   );
    //   return true;
    // }

    //if we are loading the timeout passed then we show a spinner
    if (fetchServerGridDataIsLoading && timeOutPeriodPassed) {
      //get the seconds between the current time and the timer started date time
      //if the seconds between the two dates is greater than the seconds to show loading spinner, then we show the spinner

      // get t5he current date and time
      // const currentDateTime = new Date().toISOString();

      return true;
    }

    return false;
  }

  useEffect(() => {
    if (isSuccess) {
      // Code to execute after data is successfully fetched

      if (hasDelayedLoadingSpinner) {
        setTransitionDisplayData(fetchedServerGridData);
      }

      //NOTE TODO: Okay wow so this does actually work
      //I think I want to have a means of capturing if some part of the data changes, and if it doesn't then don't modify the values

      if (setServerGridParentData && fetchedServerGridData) {
        //console.log(fetchedServerGridData);
        setServerGridParentData(fetchedServerGridData);
      }
    }
  }, [isSuccess, fetchedServerGridData]);

  useEffect(() => {
    if (headerCheckBox) {
      setHeaderCheckBox(false);
    }
  }, [gridDataState]);

  const onHeaderSelectionChange = (event) => {
    const checkboxElement = event.syntheticEvent.target;
    const checked = checkboxElement.checked;

    setHeaderCheckBox(checked);

    if (checked) {
      SelectAll();
    } else {
      UnSelectAll();
    }

    //alert("onHeaderSelectionChange: " + checked);
  };

  const ShouldDisableToolbarButton = (
    shouldDisableWhenNoRecordSelectedOption
  ) => {
    // If the option is not true, don't disable
    if (shouldDisableWhenNoRecordSelectedOption !== true) return false;

    // If there are no records in the grid, disable the button
    if (!gridResultState || !gridResultState.length) return true;

    // If any records are selected, enable the button
    var selectedKeys = GetSelectedGridDataKeys();
    if (selectedKeys.length === 0) return true;

    return false;
  };

  function onExportCompleted() {
    ExportHasCompleted();
  }

  function GetAllExportDataCallback(exportFormat) {
    if (!_grid.current) return; // If the grid reference is not available, do nothing

    InitiateGridExport(exportFormat, _grid.current.columns);
  }

  //This is the data that will be bound to the grid
  //it will be either the serverGridData or the transitionDisplayData (while we are waiting for the new data to load)
  const gridBindingData = useMemo(() => {
    let total = 0;

    let bindingData = fetchedServerGridData || transitionDisplayData;

    // if bindingdata has a total, then use that

    if (bindingData && bindingData.total) {
      total = bindingData.total;
    }

    return { bindingData, total };
  }, [fetchedServerGridData, transitionDisplayData]);

  //This will identify if the endpoint and parames have changed so we casn track the data loading status and issue callback when the data has loaded
  const gridApiArgsHash = useMemo(() => {
    if (IsNullOrEmpty(props.apiGetEndpoint)) return "";
    if (IsNullOrEmpty(props.apiSubmittedFormData)) return "";

    let hash = GetObjectHashValue(
      props.apiGetEndpoint + JSON.stringify(props.apiSubmittedFormData)
    );

    return hash;
  }, [props.apiGetEndpoint, props.apiSubmittedFormData]);

  //This will identify if the grid data state has changed so we can track the data loading status and issue callback when the data has loaded per the grid data state
  const gridDataStateHash = useMemo(() => {
    let hash = GetObjectHashValue(gridDataState);

    return hash;
  }, [gridDataState]);

  let showSpinner = shouldShowSpinner();

  useEffect(() => {
    //Issue the loading spinner callback to the parent component
    if (onGridIsLoadingStatusChanged) {
      onGridIsLoadingStatusChanged(showSpinner);
    }

    if (showSpinner) {
      //if we are showing the spinner, then we need to ensure that the gridApiParamsDataLoaded is set to false
      if (gridApiParamsDataLoaded) {
        setGridApiParamsDataLoaded(false);
      }

      return;
    }

    //alert("Api Data loaded");

    //if we are not showing the spinner, then we need to set the gridApiParamsDataLoaded to true
    if (!gridApiParamsDataLoaded) {
      //alert("Grid API Params Data Loaded");
      setGridApiParamsDataLoaded(true);
    }

    //issue the callback to the parent component that the data has loaded fopr the grid API parameters
    if (
      onGridApiDataLoadedCallback &&
      gridApiDataLoadedCallbackKey !== gridApiArgsHash // adding a check to ensure that the callback is not called multiple times for the same key
    ) {
      setGridApiDataLoadedCallbackKey(gridApiArgsHash);
      onGridApiDataLoadedCallback();
    }
  }, [gridApiArgsHash, showSpinner]);

  useEffect(() => {
    if (showSpinner) return;

    //alert("Grid Data State Changed: " + gridDataStateHash);

    //Issue the loading spinner callback to the parent component on the data state change
    if (
      onDataStateChangedCallback &&
      gridDataStateChangedCallbackKey !== gridDataStateHash // adding a check to ensure that the callback is not called multiple times for the same key
    ) {
      setGridDataStateChangedCallbackKey(gridDataStateHash);
      onDataStateChangedCallback();
    }
  }, [gridDataStateHash, showSpinner]);

  function delayedSpinnerLogicApplies() {
    if (!hasDelayedLoadingSpinner) return false; //if not delayed loading spinner, then delayed spinner logic does not apply

    if (isRefetching) return false; // If the data is being refetched, then delayed spinner logic does not apply

    if (runningExportData) return false; // If the export is running, then delayed spinner logic does not apply

    if (!gridApiParamsDataLoaded) return false; // if initial api data is not loaded, then delayed spinner logic does not apply

    return true; // delayed spinner logic applies
  }

  //run every secondd to check if the loading spinner should be shown or not
  useInterval(
    () => {
      setTimerTickPeriodPassed(new Date().toISOString());
    },
    delayedSpinnerLogicApplies() && !IsNullOrEmpty(timerStartedDateTime)
      ? 1000
      : null
  );

  //Delayed loading spinner logic is in the useEffect below
  useEffect(() => {
    function WhenDataIsLoading() {
      // This function will be called when data is loading

      //console.log("Data is loading...");

      if (IsNullOrEmpty(timerStartedDateTime)) {
        //Make sure the timer started date time is set to the current date and time
        setTimerStartedDateTime(new Date().toISOString()); // Set the timer started date time to the current date and time

        return; // If the timer started date time is not set, then do nothing
      }

      //timer is running, so we need to see how kany seconds since it was started top determineif the timeOutPeriodPassed is true or not

      let secondsSinceTimerStarter = GetSecondsBetweenDates(
        new Date(timerStartedDateTime),
        new Date()
      );

      //If number of seconds since the timer started is greater than the seconds to show loading spinner, then we need to set state that timeOutPeriodPassed to true
      if (secondsSinceTimerStarter > secondsBeforeLoadingSpinner) {
        if (!timeOutPeriodPassed) setTimeOutPeriodPassed(true);
      }
    }
    function WhenDataIsNotLoading() {
      // This function will be called when data is not loading
      // You can add your logic here to handle the non-loading state
      //console.log("Data is not loading...");

      if (!IsNullOrEmpty(timerStartedDateTime)) {
        setTimerStartedDateTime(""); // Reset the timer started date time to null
      }

      if (setTimeOutPeriodPassed) setTimeOutPeriodPassed(false);
    }

    if (!delayedSpinnerLogicApplies()) return; // If the delayed spinner logic does not apply, then do nothing

    //We have a loading spinner delay, so we need to check if the data has loaded and if so, then we need to clear the timeout

    if (fetchServerGridDataIsLoading) {
      WhenDataIsLoading();
    } else {
      WhenDataIsNotLoading();
    }
  }, [fetchServerGridDataIsLoading, timerTickPeriodPassed]);

  //this will expose target methods to the parent component
  useImperativeHandle(ref, () => ({
    GetAllGridData() {
      return fetchedServerGridData;
    },
    GetSelectedGridDataKeys() {
      return GetSelectedGridDataKeys();
    },
  }));

  const enhancedChildren = CreateEnhancedChildrenForFirefox(props.children);

  //if error then return the error component and don't proceed
  if (isError && error.description) {
    return (
      <div>
        <Alert variant={"warning"} dismissible>
          {typeof error.description === 'object' ? JSON.stringify(error.description) : error.description}
        </Alert>
      </div>
    );
  }

  return (
    <React.Fragment>
      {/* We always need the ServerGridExport to render to export the data - if we don't render it the export will now work!       
          But during the export process we are showing a spinner,  we will thereforee this in a DIV and simply set it to invisible.  
          This way we can show a spinner and export without the export buttons being visible        
        */}
      <div
        className={
          showSpinner ? "hidden-div" : "visible-element row row-cols-auto"
        }
      >
        <GridExports
          noGridExportOption={exportReportOptions.noGridExportOption}
          exportFileNamePrefix={exportReportOptions.exportFileNamePrefix}
          exportEnabled={
            IsNullOrEmpty(gridBindingData.bindingData) ? false : true
          }
          exportData={fullExportData}
          exportFormat={exportFormat}
          exportDataColumns={exportColumns}
          exportInitiated={runningExportData}
          initiateExportCallback={GetAllExportDataCallback}
          exportCompletedCallback={onExportCompleted}
          aggregateColumnSettings={enableExportAggregates ? aggregateColumnSettings : null}
        />
      </div>

      {showSpinner ? (
        <ControlSpinner />
      ) : (
        <React.Fragment>
          <Grid
            style={styleOverride ? styleOverride : { height: "700px" }}
            ref={_grid}
            //data={gridBindingData.bindingData}

            data={gridResultState}
            pageable
            sortable
            filterable
            onDataStateChange={onGridDataStateChange}
            onSelectionChange={onSelectionChange}
            total={gridBindingData.total}
            dataItemKey={dataItemKey}
            selectedField={SELECTED_FIELD}
            selectable={
              isSelectable
                ? {
                  enabled: false,
                  drag: false,
                  cell: false,
                  mode: "multiple",
                }
                : false
            }
            onHeaderSelectionChange={onHeaderSelectionChange}
            {...gridDataState}
          >
            <GridToolbar>
              {gridButtons &&
                gridButtons.map((thisButton) => (
                  <button
                    key={thisButton.text
                      .replace(/\s+/g, "")
                      .trim()
                      .toLowerCase()}
                    title={thisButton.text}
                    className={
                      thisButton.styleOverride || defaultButtonStyleClasses
                    }
                    disabled={ShouldDisableToolbarButton(
                      thisButton.disableWhenNoRecordSelected
                    )}
                    onClick={() => {
                      thisButton.callback(GetSelectedGridDataKeys());
                    }}
                  >
                    {thisButton.text}
                  </button>
                ))}
            </GridToolbar>

            {isSelectable && (
              <GridColumn
                field={SELECTED_FIELD}
                // checked={true}
                width={"50px"}
                filterable={false}
                headerSelectionValue={headerCheckBox}
              />
            )}
            {enhancedChildren}
          </Grid>
        </React.Fragment>
      )}
    </React.Fragment>
  );
});
export default SharedMainSelectableServerGrid;
//This method takes in a currentArray, buttonText, and buttonCallback and returns a new array with the button added to the end of the currentArray
export function AddGridButton(
  currentArray,
  buttonText,
  buttonCallback,
  disableWhenNoRecordSelected = true,
  styleOverride = null
) {
  let newArrayItem = {
    text: buttonText,
    callback: buttonCallback,
    disableWhenNoRecordSelected: disableWhenNoRecordSelected,
    styleOverride: styleOverride,
  };

  let newArray = [...currentArray, newArrayItem];

  return newArray;
}
